import { Metamodel } from 'app/model/base/model';

import {
    ActionConstants,
    AdapterContext,
    IModel,
    IResourceContext,
    ModelConstants,
    ValidationConstants
} from 'shell-models-lib';
import { PerformanceEvent } from 'app/logger/logger.performance-event';
import { LoggerService } from 'app/logger/logger.service';
import { loggerConstants } from 'app/logger/logger.constants';
import { DataFlowFunction } from 'app/model/dataflow/dataflow-function';
import { DataFlowUserDefinedArgument } from 'app/model/dataflow//dataflow-expression-utilities';
import { DataFlowDataType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { UserDefinedFunction } from 'app/model/dataflow/user-defined-function';
import { IDataFlow, IDataFlowPreviewProperties, IDataFlowProperties } from 'app/model/adapters/dataflow/IDataflow';
import { UserDefinedFunctionLibraryParser } from 'app/model/adapters/dataflow/user-defined-function-library.parser';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { ParseScriptMethod } from 'app/model/adapters/dataflow/dataflow.parser';
import { MessagingService, MessagingServiceChannel } from 'app/services/messaging/messaging-service';
import { Message, MessageKind } from 'app/services/messaging/message';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

@Metamodel.model({
    name: ModelConstants.udflibrary,
    display: [stringResources.UDFLibrary, stringResources.UDFLibraries],
    reusable: true,
    referable: true,
    constraints: {
        name: {
            required: true,
            pattern: ValidationConstants.factoryEntityNameValidator,
            uniqueModelName: true
        }
    },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.dataFlowsWrite
        },
        deleteResource: {
            action: ActionConstants.dataFlowsDelete
        }
    }
})
export class UserDefinedFunctionLibrary extends DataFlow {

    public static readonly MODEL: IModel;

    public libraryFunctions: UserDefinedFunction[] = [];

    public addFunction(newFunction: UserDefinedFunction): void {
        this.libraryFunctions.push(newFunction);
    }

    public editFunction(editedFunction: UserDefinedFunction, index: number): void {
        this.libraryFunctions.splice(index, 1, editedFunction);
    }

    public async parseUdfDslAsync(context: AdapterContext, resource?: IDataFlow): Promise<void> {
        const dataflowParser = this.getParser();
        const props = (resource && (resource.properties as IDataFlowProperties).typeProperties as IDataFlowPreviewProperties);

        if (!props) {
            return;
        }

        if (props.scriptLines && !props.script) {
            props.script = `functions{\n${props.scriptLines.map((line) => line.replace(/^(     )+/, (result) => '\t'.repeat((result.length) / 5))).join('\n')}}`;
        }

        const loggerService = await getServiceInstance<LoggerService>(context.contextData, 'LoggerService');
        const startTime = performance.now();

        if (props.script) {
            const scriptProps = await dataflowParser.parseDataFlowScriptAsync(context.contextData, props.script, this, {fastQueue: false});
            this.addFunctions(scriptProps.functions);
            loggerService.logEvent(new PerformanceEvent(Metamodel.modelOf(this).name, loggerConstants.Operation_ParseUdfDslAsync,
            (performance.now() - startTime), scriptProps.functions.size.toString()));
        }
    }

    public addFunctions(functions: Set<DataFlowFunction>): UserDefinedFunctionLibrary {
        functions.forEach((func: UserDefinedFunction) => {
            func.functionArgs = func.paramTypes.map((paramType, index) => new DataFlowUserDefinedArgument(`i${index + 1}`, paramType as DataFlowDataType)).slice(0, -1);
            func.funcExpression.returnType = DataFlowTypesUtils.createType(func.returnType);
            func.funcExpression.isUserDefinedFunction = true;
            this.libraryFunctions.push(func);
        });
        return this;
    }

    protected getParser(): UserDefinedFunctionLibraryParser {
        return new UserDefinedFunctionLibraryParser();
    }

    protected async _parseDSLTransformations(contextData: IResourceContext): Promise<void> {
        this.hasParsed = true;
        await this.parseDslAsync(AdapterContext.defaultContextOf(contextData), undefined, {method: ParseScriptMethod.TRANSFORMATIONS, parserVersion:'v1'});
        const msgSvc = await getServiceInstance<MessagingService>(contextData, 'MessagingService');
        msgSvc.broadcast(MessagingServiceChannel.DataFlowUpdateCanvas, new Message(MessageKind.Entity, this));
    }
}
